/**
 * Minimal client for the BetterAuth endpoints on the API. M2 surface:
 * signIn, signOut, getSession. Full auth UI (signup-completion,
 * password reset, 2FA enrollment) lands in M2 follow-ups; M2-C just
 * proves the login + protected-route round-trip.
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8787';

export interface SignInResult {
  ok: boolean;
  error?: string;
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const res = await fetch(`${API_BASE}/api/auth/sign-in/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  if (res.ok) return { ok: true };
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  return { ok: false, error: body?.message ?? `Sign-in failed (${res.status})` };
}

export async function signOut(): Promise<void> {
  await fetch(`${API_BASE}/api/auth/sign-out`, {
    method: 'POST',
    credentials: 'include',
  });
}

export interface CurrentSession {
  user: { id: string; email: string; name: string | null };
}

export async function getSession(): Promise<CurrentSession | null> {
  const res = await fetch(`${API_BASE}/api/auth/get-session`, {
    credentials: 'include',
  });
  if (!res.ok) return null;
  const body = (await res.json().catch(() => null)) as CurrentSession | null;
  return body && 'user' in body ? body : null;
}

export interface DashboardMe {
  merchant: { id: string; legalName: string; status: string };
  user: { email: string; role: string };
}

export async function fetchDashboardMe(): Promise<DashboardMe | null> {
  const res = await fetch(`${API_BASE}/v1/dashboard/me`, { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`Dashboard fetch failed (${res.status})`);
  return (await res.json()) as DashboardMe;
}
